import { relayAgeText, relayEpochAge, shortKey } from './healthFormat';
import { windowLabel } from './rejectWindows';
import type { ReadFailEntry, RejectSnapshot, Write429Entry } from './types';

// Three at-a-glance failure counters — the top-of-page summary row. Each card is
// a count + status + one line of the dominant detail, for a failure class with
// its own source: rejected PLACEMENTS (durable, trades table), write-429s
// (in-mem tape), and read-endpoint 502s (in-mem tape). The full Rejected-orders
// forensic panel + cap inspector lives directly below this row; the two
// in-memory tapes are summarized here only (their standalone tables were dropped).
export function FailCard({
  title,
  count,
  tone,
  badge,
  badgeTone,
  sub1,
  sub2,
}: {
  title: string;
  count: number | string;
  tone: string;
  badge?: string;
  badgeTone?: string;
  sub1?: string;
  sub2?: string;
}) {
  return (
    <div className="border-border rounded-lg border p-3">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-muted-foreground text-xs font-semibold">{title}</h3>
        {badge ? <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${badgeTone}`}>{badge}</span> : null}
      </div>
      <div className={`mt-1 font-mono text-2xl font-bold ${tone}`}>{count}</div>
      {sub1 ? <div className="text-muted-foreground text-[11px]">{sub1}</div> : null}
      {sub2 ? (
        <div className="text-foreground/80 mt-0.5 truncate text-[11px]" title={sub2}>
          {sub2}
        </div>
      ) : null}
    </div>
  );
}

export function FailureCards({
  rej,
  rejErr,
  rejWindowMs,
  write429,
  readFailures,
  serverTs,
}: {
  rej: RejectSnapshot | null;
  rejErr: string | null;
  rejWindowMs: number;
  write429: Write429Entry[];
  readFailures: ReadFailEntry[];
  serverTs: number;
}) {
  const winLabel = windowLabel(rejWindowMs);
  const topReject = rej?.byError?.[0];

  // Largest read-fail group (account · endpoint · status) so the card names the
  // dominant offender, not just a raw event count.
  let rfTop: { key: string; n: number } | null = null;
  const rfGroups = new Map<string, number>();
  for (const e of readFailures) {
    const who = e.username ?? e.userId.slice(0, 8);
    const k = `${who} · ${e.endpoint} · ${e.status ?? 'stall'}`;
    const n = (rfGroups.get(k) ?? 0) + 1;
    rfGroups.set(k, n);
    if (!rfTop || n > rfTop.n) rfTop = { key: k, n };
  }

  const w0 = write429[0];
  const wAcct = w0 ? (w0.username ?? shortKey(`${w0.scope ?? 'user'}:${w0.userId}`).id) : null;
  // `ts` is stamped by the relay's own Date.now() (write429Log.ts) and `serverTs` by the
  // same process, so the age uses the board's rule: a future stamp is said in words,
  // never clamped to "0s ago".
  const w0Age = w0 ? relayEpochAge(serverTs, w0.ts) : null;
  const w0When = !w0Age || w0Age.kind === 'missing' ? '429 time not reported' : relayAgeText(w0Age, '429');

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <FailCard
        title="Rejected Kalshi orders"
        badge="DURABLE"
        badgeTone="bg-red-600/80 text-white"
        count={rej ? rej.total : rejErr ? '—' : '…'}
        tone={rej && rej.total > 0 ? 'text-red-400' : rej ? 'text-green-400' : 'text-muted-foreground'}
        sub1={rejErr ? 'load failed' : `last ${winLabel}`}
        sub2={
          topReject ? `top ${topReject.reason} ×${topReject.count}` : rej && rej.total === 0 ? 'none 👍' : undefined
        }
      />
      <FailCard
        title="Recent write 429s"
        badge="IN-MEM"
        badgeTone="bg-muted text-muted-foreground"
        count={write429.length}
        tone={write429.length > 0 ? 'text-amber-400' : 'text-green-400'}
        sub1="last 20 · cleared on restart"
        sub2={
          w0
            ? `${wAcct} · ${w0.op}${w0.action ? ` ${w0.action}` : ''} · ${w0When}`
            : 'none 👍'
        }
      />
      <FailCard
        title="Read-endpoint failures"
        badge="IN-MEM"
        badgeTone="bg-muted text-muted-foreground"
        count={readFailures.length}
        tone={readFailures.length > 0 ? 'text-amber-400' : 'text-green-400'}
        sub1="last 50 · cleared on restart"
        sub2={rfTop ? `${rfTop.key} ×${rfTop.n}` : 'none 👍'}
      />
    </div>
  );
}
